/**
 * 2D Conformal (Helmert) Transformation Implementation
 *
 * This module implements the 4-parameter conformal transformation:
 * - Scale factor
 * - Rotation angle
 * - Translation in E direction
 * - Translation in N direction
 *
 * The transformation equations are:
 * E2 = a * E1 + b * N1 + c
 * N2 = -b * E1 + a * N1 + d
 *
 * Where:
 * a = scale * cos(rotation)
 * b = scale * sin(rotation), with positive rotation clockwise
 * c = translation_E
 * d = translation_N
 *
 * Coordinates arrive as strings and are parsed once into `Observation`s at
 * this boundary. `fitObservations` is the numeric core shared by the
 * ordinary solve and by blunder-detection trial fits, so every trial uses
 * exactly the same mode rules, weights, and statistics as an accepted solve.
 */

import type {
  PointPair,
  PointResidual,
  PointUncertainty,
  SolutionMode,
  TransformationSolution,
} from '@/types';
import { getWeightStatus, isFixed, isIgnored, isInFit } from '../weights';

// ============================================================================
// Types
// ============================================================================

export interface HelmertParameters {
  a: number;
  b: number;
  c: number;
  d: number;
}

/** A valid point pair parsed to numbers. */
export interface Observation {
  id: number;
  e1: number;
  n1: number;
  e2: number;
  n2: number;
}

interface PlanePoint {
  e: number;
  n: number;
}

/**
 * Result of the numeric core. Residuals are computed about `origin1` /
 * `origin2` (the weighted centroids, or the first Fixed pair), which keeps
 * them accurate for large state-plane coordinates.
 */
export interface CoreFit {
  mode: SolutionMode;
  params: HelmertParameters;
  scale: number;
  /** Degrees, positive clockwise, normalized to (−180°, 180°] */
  rotation: number;
  scaleFixed: boolean;
  parameterCount: number;
  origin1: PlanePoint;
  origin2: PlanePoint;
  /** Σ w over the pairs that estimate the parameters (standard mode) */
  sumW: number;
  /** Σ w·d² of source points about origin1 over estimating pairs */
  sumWeightedSquaredDistance: number;
  /** Indices (into the fitted arrays) of Fixed pairs held exactly */
  anchorIndices: number[];
}

export type CoreFitResult = { fit: CoreFit } | { error: string };

/** Residual statistics of the pairs that test a fit. */
export interface FitStatistics {
  redundancy: number;
  rmsN: number;
  rmsE: number;
  rmsTotal: number;
  /** Undefined when redundancy ≤ 0 */
  sigma0?: number;
}

const RAD_TO_DEG = 180 / Math.PI;
const SINGULAR_TOLERANCE = 1e-12;
const COINCIDENT_TOLERANCE = 1e-10;

export const MINIMUM_PAIRS_MESSAGE =
  'Minimum of 2 Point Pairs with Unity or Fixed status required for a solution';

// ============================================================================
// Parsing and small helpers
// ============================================================================

/**
 * Parse a coordinate string to a number, returning NaN if invalid
 */
const parseCoord = (value: string): number => {
  const num = parseFloat(value);
  return isNaN(num) ? NaN : num;
};

/**
 * Apply Resolve's positive-clockwise 2D Helmert transform.
 */
export const applyHelmertTransform = (
  n1: number,
  e1: number,
  { a, b, c, d }: HelmertParameters
): { n2: number; e2: number } => ({
  e2: a * e1 + b * n1 + c,
  n2: -b * e1 + a * n1 + d,
});

const computeHelmertTranslations = (
  source: PlanePoint,
  target: PlanePoint,
  { a, b }: Pick<HelmertParameters, 'a' | 'b'>
): Pick<HelmertParameters, 'c' | 'd'> => ({
  c: target.e - a * source.e - b * source.n,
  d: target.n + b * source.e - a * source.n,
});

/**
 * Check if a point pair has all required coordinates
 */
export const isValidPointPair = (p: PointPair): boolean => {
  return (
    p.n1 !== '' &&
    p.e1 !== '' &&
    p.n2 !== '' &&
    p.e2 !== '' &&
    !isNaN(parseCoord(p.n1)) &&
    !isNaN(parseCoord(p.e1)) &&
    !isNaN(parseCoord(p.n2)) &&
    !isNaN(parseCoord(p.e2))
  );
};

/** Parse a valid pair; null when any coordinate is missing or invalid. */
const toObservation = (p: PointPair): Observation | null => {
  if (!isValidPointPair(p)) return null;
  return {
    id: p.id,
    e1: parseCoord(p.e1),
    n1: parseCoord(p.n1),
    e2: parseCoord(p.e2),
    n2: parseCoord(p.n2),
  };
};

/**
 * Get the point status string from weight
 */
export const getPointStatus = getWeightStatus;

/** Normalize an angle in degrees to the interval (−180°, 180°]. */
export const normalizeRotation = (degrees: number): number => {
  let angle = degrees % 360;
  if (angle <= -180) angle += 360;
  else if (angle > 180) angle -= 360;
  return angle === 0 ? 0 : angle; // no negative zero
};

/** The solution mode of a solved transformation (older solutions lack `mode`). */
export const getSolutionMode = (
  solution: TransformationSolution
): SolutionMode =>
  solution.mode ??
  (solution.constrained
    ? 'constrained'
    : solution.minimallyConstrained
      ? 'minimallyConstrained'
      : 'standard');

/** Observations and weights of the valid pairs that enter the fit. */
export const collectFitObservations = (
  points: readonly PointPair[]
): { observations: Observation[]; weights: number[] } => {
  const observations: Observation[] = [];
  const weights: number[] = [];
  points.forEach((p) => {
    if (!isInFit(p.weight)) return;
    const observation = toObservation(p);
    if (!observation) return;
    observations.push(observation);
    weights.push(p.weight);
  });
  return { observations, weights };
};

// ============================================================================
// Weighted geometry
// ============================================================================

interface WeightedCentroid {
  sumW: number;
  source: PlanePoint;
  target: PlanePoint;
}

/** Weighted centroid of the source and target points; null when Σw ≤ 0. */
const computeWeightedCentroid = (
  observations: readonly Observation[],
  weights: readonly number[]
): WeightedCentroid | null => {
  let sumW = 0;
  let e1 = 0;
  let n1 = 0;
  let e2 = 0;
  let n2 = 0;
  observations.forEach((o, i) => {
    const w = weights[i];
    sumW += w;
    e1 += w * o.e1;
    n1 += w * o.n1;
    e2 += w * o.e2;
    n2 += w * o.n2;
  });
  if (!(sumW > 0)) return null;
  return {
    sumW,
    source: { e: e1 / sumW, n: n1 / sumW },
    target: { e: e2 / sumW, n: n2 / sumW },
  };
};

/**
 * Weighted sums about reference points: D = Σ w·|p1 − o1|², and the
 * cross sums A = Σ w·(x1·x2 + y1·y2), B = Σ w·(y1·x2 − x1·y2) that give the
 * least-squares a = A / D and b = B / D.
 */
const weightedReducedSums = (
  observations: readonly Observation[],
  weights: readonly number[],
  origin1: PlanePoint,
  origin2: PlanePoint,
  skip?: ReadonlySet<number>
): { D: number; A: number; B: number } => {
  let D = 0;
  let A = 0;
  let B = 0;
  observations.forEach((o, i) => {
    if (skip?.has(i)) return;
    const w = weights[i];
    const x1 = o.e1 - origin1.e;
    const y1 = o.n1 - origin1.n;
    const x2 = o.e2 - origin2.e;
    const y2 = o.n2 - origin2.n;
    D += w * (x1 * x1 + y1 * y1);
    A += w * (x1 * x2 + y1 * y2);
    B += w * (y1 * x2 - x1 * y2);
  });
  return { D, A, B };
};

/**
 * Rescale (a, b) to a held scale. Least squares with the scale fixed keeps
 * the same rotation and translation reference, so rescaling is exact.
 */
const applyFixedScale = (
  a: number,
  b: number,
  fixedScale: number | null
): { a: number; b: number } => {
  if (fixedScale === null) return { a, b };
  const currentScale = Math.hypot(a, b);
  if (!(currentScale > 0)) return { a, b };
  const factor = fixedScale / currentScale;
  return { a: a * factor, b: b * factor };
};

const buildFit = (
  mode: SolutionMode,
  a: number,
  b: number,
  origin1: PlanePoint,
  origin2: PlanePoint,
  extra: {
    scaleFixed: boolean;
    parameterCount: number;
    sumW: number;
    sumWeightedSquaredDistance: number;
    anchorIndices: number[];
    rotationRadians?: number;
  }
): CoreFit => {
  const { c, d } = computeHelmertTranslations(origin1, origin2, { a, b });
  const { rotationRadians, ...rest } = extra;
  return {
    mode,
    params: { a, b, c, d },
    scale: Math.hypot(a, b),
    rotation: normalizeRotation(
      (rotationRadians ?? Math.atan2(b, a)) * RAD_TO_DEG
    ),
    origin1,
    origin2,
    ...rest,
  };
};

// ============================================================================
// Numeric core
// ============================================================================

const fitStandard = (
  observations: readonly Observation[],
  weights: readonly number[],
  fixedScale: number | null
): CoreFitResult => {
  const centroid = computeWeightedCentroid(observations, weights);
  if (!centroid) {
    return {
      error: 'Matrix is not Positive Definite - check point coordinates',
    };
  }
  const { D, A, B } = weightedReducedSums(
    observations,
    weights,
    centroid.source,
    centroid.target
  );
  if (Math.abs(D) < SINGULAR_TOLERANCE) {
    return {
      error: 'Matrix is not Positive Definite - check point coordinates',
    };
  }
  const { a, b } = applyFixedScale(A / D, B / D, fixedScale);
  return {
    fit: buildFit('standard', a, b, centroid.source, centroid.target, {
      scaleFixed: fixedScale !== null,
      parameterCount: fixedScale !== null ? 3 : 4,
      sumW: centroid.sumW,
      sumWeightedSquaredDistance: D,
      anchorIndices: [],
    }),
  };
};

/**
 * Two Fixed pairs: scale from the distance ratio, rotation from the bearing
 * difference, translation from the first pair. A scale override does not
 * apply in this mode.
 */
const fitConstrained = (
  observations: readonly Observation[],
  anchorIndices: number[]
): CoreFitResult => {
  const p1 = observations[anchorIndices[0]];
  const p2 = observations[anchorIndices[1]];
  const dx1 = p2.e1 - p1.e1;
  const dy1 = p2.n1 - p1.n1;
  const dx2 = p2.e2 - p1.e2;
  const dy2 = p2.n2 - p1.n2;
  const d1 = Math.hypot(dx1, dy1);
  const d2 = Math.hypot(dx2, dy2);

  if (d1 < COINCIDENT_TOLERANCE) {
    return { error: 'Fixed points in System 1 are too close together' };
  }
  if (d2 < COINCIDENT_TOLERANCE) {
    return { error: 'Fixed points in System 2 are too close together' };
  }

  const scale = d2 / d1;
  const theta = Math.atan2(dx2, dy2) - Math.atan2(dx1, dy1);
  const origin1 = { e: p1.e1, n: p1.n1 };
  const origin2 = { e: p1.e2, n: p1.n2 };
  return {
    fit: buildFit(
      'constrained',
      scale * Math.cos(theta),
      scale * Math.sin(theta),
      origin1,
      origin2,
      {
        scaleFixed: false,
        parameterCount: 0,
        sumW: 0,
        sumWeightedSquaredDistance: 0,
        anchorIndices,
        rotationRadians: theta,
      }
    ),
  };
};

/**
 * One Fixed pair: translation held by that pair; scale and rotation from a
 * weighted least-squares fit of the other pairs about it.
 */
const fitMinimallyConstrained = (
  observations: readonly Observation[],
  weights: readonly number[],
  anchorIndex: number,
  fixedScale: number | null
): CoreFitResult => {
  const anchor = observations[anchorIndex];
  const origin1 = { e: anchor.e1, n: anchor.n1 };
  const origin2 = { e: anchor.e2, n: anchor.n2 };
  const { D, A, B } = weightedReducedSums(
    observations,
    weights,
    origin1,
    origin2,
    new Set([anchorIndex])
  );
  if (Math.abs(D) < SINGULAR_TOLERANCE) {
    return { error: 'Insufficient geometry for solution' };
  }
  const { a, b } = applyFixedScale(A / D, B / D, fixedScale);
  return {
    fit: buildFit('minimallyConstrained', a, b, origin1, origin2, {
      scaleFixed: fixedScale !== null,
      parameterCount: fixedScale !== null ? 1 : 2,
      sumW: 0,
      sumWeightedSquaredDistance: D,
      anchorIndices: [anchorIndex],
    }),
  };
};

/**
 * Fit the transformation to parsed in-fit observations (weights ≥ 1).
 * Exactly two Fixed pairs give the constrained mode, one gives the
 * minimally constrained mode, and none or three or more give the standard
 * weighted least-squares solution.
 */
export const fitObservations = (
  observations: readonly Observation[],
  weights: readonly number[],
  fixedScale: number | null = null
): CoreFitResult => {
  if (observations.length < 2) return { error: MINIMUM_PAIRS_MESSAGE };
  const fixedIndices: number[] = [];
  weights.forEach((w, i) => {
    if (isFixed(w)) fixedIndices.push(i);
  });
  if (fixedIndices.length === 2) {
    return fitConstrained(observations, fixedIndices);
  }
  if (fixedIndices.length === 1) {
    return fitMinimallyConstrained(
      observations,
      weights,
      fixedIndices[0],
      fixedScale
    );
  }
  return fitStandard(observations, weights, fixedScale);
};

/** Residual (observed − computed) of an observation, in reduced form. */
export const residualOf = (
  o: Observation,
  fit: CoreFit
): { ve: number; vn: number } => {
  const { a, b } = fit.params;
  const x = o.e1 - fit.origin1.e;
  const y = o.n1 - fit.origin1.n;
  return {
    ve: o.e2 - (fit.origin2.e + a * x + b * y),
    vn: o.n2 - (fit.origin2.n - b * x + a * y),
  };
};

/** Azimuth of a residual vector, clockwise from north in [0, 360). */
export const residualAzimuth = (ve: number, vn: number): number =>
  (((Math.atan2(ve, vn) * RAD_TO_DEG) % 360) + 360) % 360;

/**
 * Redundancy and residual statistics of a fit. Fixed pairs held exactly in
 * the one- and two-Fixed modes have no residual freedom and are left out;
 * every other in-fit pair contributes its two components.
 *
 * r = 2m − u, with m the contributing pairs and u the estimated parameters
 * (4 standard, 3 standard with fixed scale, 2 or 1 with one Fixed pair, 0
 * with two). Per-axis RMS divides by r / 2, so rmsTotal² = (rmsN² + rmsE²)/2.
 */
export const computeFitStatistics = (
  observations: readonly Observation[],
  weights: readonly number[],
  fit: CoreFit
): FitStatistics => {
  const anchors = new Set(fit.anchorIndices);
  let sumNN = 0;
  let sumEE = 0;
  let sumWeighted = 0;
  let count = 0;
  observations.forEach((o, i) => {
    if (anchors.has(i)) return;
    const { ve, vn } = residualOf(o, fit);
    sumNN += vn * vn;
    sumEE += ve * ve;
    sumWeighted += weights[i] * (vn * vn + ve * ve);
    count++;
  });
  const redundancy = 2 * count - fit.parameterCount;
  if (redundancy <= 0) {
    // An exact fit: the residuals are zero apart from rounding and carry
    // no statistical information.
    return {
      redundancy: Math.max(redundancy, 0),
      rmsN: 0,
      rmsE: 0,
      rmsTotal: 0,
    };
  }
  return {
    redundancy,
    rmsN: Math.sqrt(sumNN / (redundancy / 2)),
    rmsE: Math.sqrt(sumEE / (redundancy / 2)),
    rmsTotal: Math.sqrt((sumNN + sumEE) / redundancy),
    sigma0: Math.sqrt(sumWeighted / redundancy),
  };
};

/**
 * Standard deviations of scale and rotation from σ̂₀ and the weighted
 * normal matrix (a and b each have variance σ̂₀² / D). Scale has none when it
 * is held fixed; the two-Fixed mode estimates neither.
 */
const parameterDeviations = (
  fit: CoreFit,
  sigma0: number | undefined
): { sigmaScale?: number; sigmaRotation?: number } => {
  if (
    sigma0 === undefined ||
    fit.mode === 'constrained' ||
    !(fit.sumWeightedSquaredDistance > 0)
  ) {
    return {};
  }
  const sigmaParameter = sigma0 / Math.sqrt(fit.sumWeightedSquaredDistance);
  return {
    sigmaScale: fit.scaleFixed ? undefined : sigmaParameter,
    sigmaRotation:
      fit.scale > 0 ? (sigmaParameter / fit.scale) * RAD_TO_DEG : undefined,
  };
};

// ============================================================================
// Solution assembly
// ============================================================================

/** A failed solution with every numeric field zeroed. */
export const createFailedSolution = (
  error: string
): TransformationSolution => ({
  error,
  a: 0,
  b: 0,
  c: 0,
  d: 0,
  scale: 0,
  rotation: 0,
  translationE: 0,
  translationN: 0,
  residuals: [],
  rmsN: 0,
  rmsE: 0,
  rmsTotal: 0,
  pointCount: 0,
  redundancy: 0,
});

const computePointResidual = (p: PointPair, fit: CoreFit): PointResidual => {
  const observation = toObservation(p);
  if (!observation) {
    return {
      id: p.id,
      vn: null,
      ve: null,
      total: null,
      excluded: true,
      status: 'incomplete',
    };
  }

  if (isIgnored(p.weight)) {
    return {
      id: p.id,
      vn: null,
      ve: null,
      total: null,
      excluded: true,
      status: 'ignore',
    };
  }

  const { ve, vn } = residualOf(observation, fit);
  return {
    id: p.id,
    vn,
    ve,
    total: Math.hypot(ve, vn),
    azimuth: residualAzimuth(ve, vn),
    excluded: !isInFit(p.weight),
    status: getPointStatus(p.weight),
  };
};

/**
 * Assemble the public solution for a successful core fit. Residuals are
 * returned for every pair in input order (null for Ignore and incomplete).
 */
export const buildTransformationSolution = (
  points: readonly PointPair[],
  observations: readonly Observation[],
  weights: readonly number[],
  fit: CoreFit
): TransformationSolution => {
  const statistics = computeFitStatistics(observations, weights, fit);
  const { a, b, c, d } = fit.params;
  return {
    a,
    b,
    c,
    d,
    scale: fit.scale,
    rotation: fit.rotation,
    translationE: c,
    translationN: d,
    residuals: points.map((p) => computePointResidual(p, fit)),
    rmsN: statistics.rmsN,
    rmsE: statistics.rmsE,
    rmsTotal: statistics.rmsTotal,
    pointCount: observations.length,
    redundancy: statistics.redundancy,
    sigma0: statistics.sigma0,
    ...parameterDeviations(fit, statistics.sigma0),
    mode: fit.mode,
    scaleFixed: fit.scaleFixed,
    parameterCount: fit.parameterCount,
    ...(fit.mode === 'standard' && {
      centroid: {
        x: fit.origin1.e,
        y: fit.origin1.n,
        x2: fit.origin2.e,
        y2: fit.origin2.n,
      },
    }),
    ...(fit.mode === 'constrained' && { constrained: true }),
    ...(fit.mode === 'minimallyConstrained' && { minimallyConstrained: true }),
  };
};

/**
 * Solve the conformal transformation using weighted least squares
 *
 * @param points - Array of point pairs
 * @param fixedScale - Optional fixed scale factor (null for computed)
 * @returns TransformationSolution with parameters and residuals
 */
export const solveConformalTransformation = (
  points: PointPair[],
  fixedScale: number | null = null
): TransformationSolution => {
  const { observations, weights } = collectFitObservations(points);
  const result = fitObservations(observations, weights, fixedScale);
  if ('error' in result) return createFailedSolution(result.error);
  return buildTransformationSolution(points, observations, weights, result.fit);
};

// ============================================================================
// Error Ellipse Computation
// ============================================================================

/**
 * Chi-square critical value for 95% confidence with 2 degrees of freedom.
 * sqrt(5.991) ≈ 2.4477
 */
const K95 = Math.sqrt(5.991);

/**
 * Compute 95% confidence ellipse parameters for each point's transformed position.
 *
 * For a standard conformal transformation with centralized coordinates, the
 * covariance matrix at each point is isotropic (equal in all directions),
 * producing confidence circles rather than ellipses. The radius depends on:
 *   - Distance from the centroid (farther = more uncertain)
 *   - The a-posteriori standard error of unit weight σ̂₀
 *   - Sum of weights and sum of weighted squared distances from centroid
 *
 * With a fixed scale the radial term is smaller than shown; the circle uses
 * the larger, tangential value. Solutions saved before σ̂₀ was reported fall
 * back to rmsTotal (identical for Unity weights).
 *
 * Returns null for constrained/minimally constrained solutions, and when
 * there is no redundancy to estimate σ̂₀.
 */
export const computePointUncertainties = (
  points: PointPair[],
  solution: TransformationSolution
): PointUncertainty[] | null => {
  if (
    solution.error ||
    solution.constrained ||
    solution.minimallyConstrained ||
    !(solution.redundancy > 0)
  ) {
    return null;
  }
  const sigma0 = solution.sigma0 ?? solution.rmsTotal;

  const { observations, weights } = collectFitObservations(points);
  const centroid = computeWeightedCentroid(observations, weights);
  if (!centroid) return null;
  const { D } = weightedReducedSums(
    observations,
    weights,
    centroid.source,
    centroid.target
  );
  if (D < SINGULAR_TOLERANCE) return null;

  const uncertainties: PointUncertainty[] = [];
  points.forEach((p) => {
    if (isIgnored(p.weight)) return;
    const o = toObservation(p);
    if (!o) return;
    const de = o.e1 - centroid.source.e;
    const dn = o.n1 - centroid.source.n;
    const sigmaPos =
      sigma0 * Math.sqrt((de * de + dn * dn) / D + 1 / centroid.sumW);
    const r95 = sigmaPos * K95;
    uncertainties.push({
      id: p.id,
      semiMajor: r95,
      semiMinor: r95,
      orientation: 0,
    });
  });

  return uncertainties;
};

/**
 * Radial standard deviation for predicting where a transformed Record point
 * lands in the Field frame, as a function of its Record coordinates.
 *
 * Per coordinate, the prediction variance is the control scatter plus the
 * propagated parameter uncertainty: RMS² + σ̂₀²·(1/Σw + d²/D), where RMS is
 * the per-component residual RMS of the fit (in ground units) and d is the
 * distance from the weighted centroid. Both terms are unchanged when every
 * weight is scaled alike, so all-Fixed and all-Unity control predict the same
 * value. The radial (2D) value multiplies by √2; about 63% of points fall
 * within it, and ×1.73 gives the 95% radius.
 *
 * Fixed-point (constrained or minimally constrained) solutions return the
 * scatter term only: their parameters are pinned to the Fixed pairs, so the
 * centroid-based propagation does not apply. Returns null without redundancy.
 */
export const createPredictionStdDev = (
  points: PointPair[],
  solution: TransformationSolution
): ((n1: number, e1: number) => number) | null => {
  if (solution.error || !(solution.redundancy > 0)) return null;
  const scatter = solution.rmsTotal;
  const scatterOnly = () => scatter * Math.SQRT2;
  if (solution.constrained || solution.minimallyConstrained) return scatterOnly;
  const sigma0 = solution.sigma0 ?? solution.rmsTotal;

  const { observations, weights } = collectFitObservations(points);
  const centroid = computeWeightedCentroid(observations, weights);
  if (!centroid) return scatterOnly;
  const { D } = weightedReducedSums(
    observations,
    weights,
    centroid.source,
    centroid.target
  );
  if (D < SINGULAR_TOLERANCE) return scatterOnly;

  return (n1, e1) => {
    const de = e1 - centroid.source.e;
    const dn = n1 - centroid.source.n;
    const parameterVariance =
      sigma0 * sigma0 * (1 / centroid.sumW + (de * de + dn * dn) / D);
    return Math.SQRT2 * Math.sqrt(scatter * scatter + parameterVariance);
  };
};

// ============================================================================
// Point Transformation
// ============================================================================

/**
 * Transform a single point using the solution parameters
 */
export const transformPoint = (
  n1: number,
  e1: number,
  solution: TransformationSolution
): { n2: number; e2: number } => {
  return applyHelmertTransform(n1, e1, solution);
};
